import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
  visible: boolean;
}

interface ToastContextValue {
  toast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, visible: false } : t)));
    setTimeout(() => removeToast(id), 200);
  }, [removeToast]);

  const toast = useCallback((message: string, type: ToastType = 'info') => {
    const id = ++nextId;
    setToasts((prev) => {
      const next = [...prev, { id, message, type, visible: false }];
      // Keep max 3 — dismiss oldest first
      while (next.length > 3) {
        const oldest = next.shift()!;
        const timer = timersRef.current.get(oldest.id);
        if (timer) {
          clearTimeout(timer);
          timersRef.current.delete(oldest.id);
        }
      }
      return next;
    });

    // Trigger slide-in on next frame
    requestAnimationFrame(() => {
      setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, visible: true } : t)));
    });

    // Auto-dismiss after 3s
    const timer = setTimeout(() => dismissToast(id), 3000);
    timersRef.current.set(id, timer);
  }, [dismissToast]);

  // Cleanup timers on unmount
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
    };
  }, []);

  const typeStyles: Record<ToastType, { borderColor: string }> = {
    success: { borderColor: 'var(--color-success)' },
    error: { borderColor: 'var(--color-error)' },
    info: { borderColor: 'var(--color-brand)' },
  };

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div style={{ position: 'fixed', bottom: '1rem', right: '1rem', zIndex: 9999, display: 'flex', flexDirection: 'column', gap: '0.5rem', pointerEvents: 'none' }}>
        {toasts.map((t) => (
          <div
            key={t.id}
            style={{
              pointerEvents: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.75rem 1rem',
              borderRadius: '0.5rem',
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderLeft: `3px solid ${typeStyles[t.type].borderColor}`,
              color: 'var(--color-text)',
              fontFamily: 'var(--font-body)',
              fontSize: '0.875rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              opacity: t.visible ? 1 : 0,
              transform: t.visible ? 'translateX(0)' : 'translateX(1rem)',
              transition: 'opacity 200ms ease, transform 200ms ease',
              maxWidth: '24rem',
            }}
          >
            <span style={{ flex: 1 }}>{t.message}</span>
            <button
              type="button"
              onClick={() => dismissToast(t.id)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.125rem',
                color: 'var(--color-text)',
                opacity: 0.5,
                fontSize: '1rem',
                lineHeight: 1,
              }}
              aria-label="Dismiss"
            >
              &times;
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const noop: ToastContextValue = { toast: () => {} };

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  return ctx ?? noop;
}
